/**
 * Cross-entity aggregator: combines `getCustomWorkflowById`
 * (entities/custom-workflow) with `useCurrentWorkspaceId`
 * (entities/workspace) to gate the per-workflow detail query on workspace
 * presence. FSD prohibits cross-entity value imports between sibling entity
 * slices, so this aggregator MUST live above the entity layer.
 *
 * Same shared-aggregator rationale as `useCustomWorkflows`,
 * `useKnowledgeBasesList`, and `useFoldersWithGuidelines`.
 *
 * Consumed by the entity-layer `CustomWorkflowInlineInput` UI
 * (entities/custom-workflow/ui) — entities → shared is allowed by the FSD
 * matrix, while entities → entities (value) is not.
 *
 * Lifted from `features/custom-workflows/model/useCustomWorkflowDetail/useCustomWorkflowDetail.ts`
 * (re-exported from old path for backward compat).
 */
import { useQuery } from '@tanstack/react-query';

import { getCustomWorkflowById } from '@/entities/custom-workflow';
import { useCurrentWorkspaceId } from '@/entities/workspace';

export const useCustomWorkflowDetail = (id: string) => {
  const selectedWorkspaceId = useCurrentWorkspaceId();
  const { data, isLoading } = useQuery({
    enabled: !!id && !!selectedWorkspaceId,
    queryKey: ['custom-workflow', id],
    queryFn: () => getCustomWorkflowById(id),
  });

  return { customWorkflow: data ?? null, isLoading };
};
